import { useState, useCallback, useEffect, useRef } from 'react';
import {
  createEmptyBoard, randomPiece, isValidPosition, rotatePiece,
  placePiece, clearLines, calculateScore, getLevel, getDropInterval,
  getGhostPosition, Board, Piece, BOARD_WIDTH,
} from './tetrisEngine';

export type GameState = 'idle' | 'playing' | 'paused' | 'gameover';

export function useTetris() {
  const [board, setBoard] = useState<Board>(createEmptyBoard());
  const [currentPiece, setCurrentPiece] = useState<Piece | null>(null);
  const [nextPiece, setNextPiece] = useState<Piece | null>(null);
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [level, setLevel] = useState(0);
  const [gameState, setGameState] = useState<GameState>('idle');

  const dropTimer = useRef<number | null>(null);
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;

  const boardRef = useRef(board);
  boardRef.current = board;
  const currentPieceRef = useRef(currentPiece);
  currentPieceRef.current = currentPiece;
  const scoreRef = useRef(score);
  scoreRef.current = score;
  const linesRef = useRef(lines);
  linesRef.current = lines;

  const spawnPiece = useCallback((next?: Piece | null) => {
    const piece = next || randomPiece();
    const newNext = randomPiece();
    
    if (!isValidPosition(boardRef.current, piece, piece.pos)) {
      setGameState('gameover');
      return;
    }
    setCurrentPiece(piece);
    setNextPiece(newNext);
  }, []);

  const lockPiece = useCallback(() => {
    const piece = currentPieceRef.current;
    if (!piece) return;

    const newBoard = placePiece(boardRef.current, piece);
    const { newBoard: clearedBoard, linesCleared } = clearLines(newBoard);

    const newLines = linesRef.current + linesCleared;
    const newLevel = getLevel(newLines);
    const addedScore = calculateScore(linesCleared, newLevel);

    setBoard(clearedBoard);
    boardRef.current = clearedBoard;
    setLines(newLines);
    linesRef.current = newLines;
    setLevel(newLevel);
    setScore(s => { const ns = s + addedScore; scoreRef.current = ns; return ns; });

    spawnPiece(nextPiece);
  }, [nextPiece, spawnPiece]);

  const moveDown = useCallback(() => {
    const piece = currentPieceRef.current;
    if (!piece || gameStateRef.current !== 'playing') return;

    const newPos = { x: piece.pos.x, y: piece.pos.y + 1 };
    if (isValidPosition(boardRef.current, piece, newPos)) {
      setCurrentPiece({ ...piece, pos: newPos });
    } else {
      lockPiece();
    }
  }, [lockPiece]);

  const moveLeft = useCallback(() => {
    const piece = currentPieceRef.current;
    if (!piece || gameStateRef.current !== 'playing') return;
    const newPos = { x: piece.pos.x - 1, y: piece.pos.y };
    if (isValidPosition(boardRef.current, piece, newPos)) {
      setCurrentPiece({ ...piece, pos: newPos });
    }
  }, []);

  const moveRight = useCallback(() => {
    const piece = currentPieceRef.current;
    if (!piece || gameStateRef.current !== 'playing') return;
    const newPos = { x: piece.pos.x + 1, y: piece.pos.y };
    if (isValidPosition(boardRef.current, piece, newPos)) {
      setCurrentPiece({ ...piece, pos: newPos });
    }
  }, []);

  const rotate = useCallback(() => {
    const piece = currentPieceRef.current;
    if (!piece || gameStateRef.current !== 'playing') return;
    const rotated = rotatePiece(piece);
    const newPiece = { ...piece, shape: rotated };
    
    // Wall kick: try offsets
    const offsets = [0, -1, 1, -2, 2];
    for (const offset of offsets) {
      const testPos = { x: piece.pos.x + offset, y: piece.pos.y };
      if (isValidPosition(boardRef.current, newPiece, testPos)) {
        setCurrentPiece({ ...newPiece, pos: testPos });
        return;
      }
    }
  }, []);

  const hardDrop = useCallback(() => {
    const piece = currentPieceRef.current;
    if (!piece || gameStateRef.current !== 'playing') return;
    const ghost = getGhostPosition(boardRef.current, piece);
    const dropped = { ...piece, pos: ghost };
    setCurrentPiece(dropped);
    currentPieceRef.current = dropped;
    lockPiece();
  }, [lockPiece]);

  const startGame = useCallback(() => {
    const emptyBoard = createEmptyBoard();
    setBoard(emptyBoard);
    boardRef.current = emptyBoard;
    setScore(0);
    scoreRef.current = 0;
    setLines(0);
    linesRef.current = 0;
    setLevel(0);
    setGameState('playing');
    const first = randomPiece();
    const second = randomPiece();
    setCurrentPiece(first);
    currentPieceRef.current = first;
    setNextPiece(second);
  }, []);

  const togglePause = useCallback(() => {
    setGameState(s => s === 'playing' ? 'paused' : s === 'paused' ? 'playing' : s);
  }, []);

  // Auto drop
  useEffect(() => {
    if (gameState !== 'playing') {
      if (dropTimer.current) clearInterval(dropTimer.current);
      return;
    }
    const interval = getDropInterval(level);
    dropTimer.current = window.setInterval(moveDown, interval);
    return () => { if (dropTimer.current) clearInterval(dropTimer.current); };
  }, [gameState, level, moveDown]);

  // Keyboard
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (gameStateRef.current !== 'playing') {
        if (e.key === ' ' && gameStateRef.current === 'idle') startGame();
        if (e.key === 'p' || e.key === 'P') togglePause();
        return;
      }
      switch (e.key) {
        case 'ArrowLeft': case 'a': e.preventDefault(); moveLeft(); break;
        case 'ArrowRight': case 'd': e.preventDefault(); moveRight(); break;
        case 'ArrowDown': case 's': e.preventDefault(); moveDown(); break;
        case 'ArrowUp': case 'w': e.preventDefault(); rotate(); break;
        case ' ': e.preventDefault(); hardDrop(); break;
        case 'p': case 'P': togglePause(); break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [moveLeft, moveRight, moveDown, rotate, hardDrop, startGame, togglePause]);

  // Build display board with ghost + current piece
  const displayBoard = (() => {
    let b = board.map(r => [...r]);
    if (currentPiece && gameState === 'playing') {
      // Ghost
      const ghost = getGhostPosition(board, currentPiece);
      for (let y = 0; y < currentPiece.shape.length; y++) {
        for (let x = 0; x < currentPiece.shape[y].length; x++) {
          if (!currentPiece.shape[y][x]) continue;
          const ny = ghost.y + y;
          const nx = ghost.x + x;
          if (ny >= 0 && ny < 20 && nx >= 0 && nx < BOARD_WIDTH && b[ny][nx] === '') {
            b[ny][nx] = 'ghost';
          }
        }
      }
      // Current piece
      for (let y = 0; y < currentPiece.shape.length; y++) {
        for (let x = 0; x < currentPiece.shape[y].length; x++) {
          if (!currentPiece.shape[y][x]) continue;
          const ny = currentPiece.pos.y + y;
          const nx = currentPiece.pos.x + x;
          if (ny >= 0 && ny < 20 && nx >= 0 && nx < BOARD_WIDTH) {
            b[ny][nx] = currentPiece.type;
          }
        }
      }
    }
    return b;
  })();

  return {
    board: displayBoard,
    score,
    lines,
    level,
    gameState,
    nextPiece,
    startGame,
    togglePause,
    moveLeft,
    moveRight,
    moveDown,
    rotate,
    hardDrop,
  };
}
